import { Link } from 'react-router-dom';
import { FiGithub, FiTwitter, FiInstagram, FiYoutube, FiArrowUp, FiMail } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [email, setEmail] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 500) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle newsletter subscription
    console.log('Subscribing email:', email);
    // Reset form
    setEmail('');
    // Show success message (in a real app)
  };

  return (
    <footer className="relative bg-dark-800 border-t border-dark-700 overflow-hidden">
      {/* Scroll to top button */}
      <AnimatedScrollButton show={showScrollTop} onClick={scrollToTop} />
      
      {/* Background elements */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary-500/20 to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary-500/20 to-transparent"></div>
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary-500/10 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-secondary-500/10 rounded-full filter blur-3xl"></div>
      </div>
      
      {/* Footer content */}
      <div className="container-custom py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {/* Logo and Description */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="col-span-1 md:col-span-5"
          >
            <Link to="/" className="flex items-center mb-4 group">
              <motion.div 
                className="h-12 w-12 bg-gradient-to-br from-primary-600 to-primary-700 rounded-lg flex items-center justify-center text-white font-bold text-xl mr-3 shadow-3d"
                whileHover={{ rotate: 5 }}
              >
                FT
              </motion.div>
              <span className="text-3xl font-bold text-gradient font-display group-hover:scale-105 transition-transform duration-300">FTTF.World</span>
            </Link>
            <p className="text-gray-400 mb-6 max-w-md">
              A decentralized media streaming and NFT platform designed for artists and fans to interact, 
              transact, and participate in a tokenized creative ecosystem.
            </p>
            <div className="flex space-x-4">
              <SocialLink href="https://github.com/fttf-world" icon={<FiGithub size={20} />} />
              <SocialLink href="https://twitter.com/fttf_world" icon={<FiTwitter size={20} />} />
              <SocialLink href="https://instagram.com/fttf.world" icon={<FiInstagram size={20} />} />
              <SocialLink href="https://youtube.com/fttfworld" icon={<FiYoutube size={20} />} />
            </div>
          </motion.div>

          {/* Navigation */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="col-span-1 md:col-span-2"
          >
            <h3 className="text-white font-bold mb-4 text-lg">Navigation</h3>
            <ul className="space-y-3">
              <FooterLink to="/">Home</FooterLink>
              <FooterLink to="/explore">Explore</FooterLink>
              <FooterLink to="/streams">Live Streams</FooterLink>
              <FooterLink to="/marketplace">Marketplace</FooterLink>
              <FooterLink to="/about">About</FooterLink>
            </ul>
          </motion.div>

          {/* Resources */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="col-span-1 md:col-span-2"
          >
            <h3 className="text-white font-bold mb-4 text-lg">Resources</h3>
            <ul className="space-y-3">
              <FooterLink to="/faucet">Token Faucet</FooterLink>
              <FooterLink to="/docs/terms.pdf" external>Terms of Service</FooterLink>
              <FooterLink to="/docs/privacy.pdf" external>Privacy Policy</FooterLink>
              <FooterLink to="/faq">FAQ</FooterLink>
              <FooterLink to="/governance">Governance</FooterLink>
            </ul>
          </motion.div>

          {/* Newsletter */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="col-span-1 md:col-span-3"
          >
            <h3 className="text-white font-bold mb-4 text-lg">Stay Updated</h3>
            <p className="text-gray-400 mb-4">Subscribe to our newsletter for the latest updates.</p>
            <form className="flex flex-col space-y-2" onSubmit={handleSubscribe}>
              <motion.div 
                className="relative shadow-inner-3d rounded-md"
                whileFocus={{ scale: 1.01 }}
              >
                <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                <input 
                  type="email" 
                  placeholder="Your email address" 
                  className="input pl-10 w-full bg-dark-700 border-dark-600 focus:ring-primary-500 focus:border-transparent rounded-md"
                  aria-label="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </motion.div>
              <motion.button 
                type="submit" 
                className="btn-primary shadow-3d w-full"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Subscribe
              </motion.button>
            </form>
          </motion.div>
        </div>

        {/* Copyright */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-16 pt-8 border-t border-dark-700 flex flex-col md:flex-row justify-between items-center text-gray-500"
        >
          <p>© {currentYear} FTTF.World. All rights reserved.</p>
          <div className="flex items-center mt-4 md:mt-0">
            <p className="text-sm">
              Powered by{' '}
              <a
                href="https://livepeer.org"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gradient hover:opacity-80 transition-opacity"
              >
                Livepeer
              </a>
              {' & '}
              <a
                href="https://ipfs.io"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gradient hover:opacity-80 transition-opacity"
              >
                IPFS
              </a>
            </p>
          </div>
        </motion.div>
      </div>
      
      {/* Background gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-500/20 to-transparent"></div>
    </footer>
  );
};

// Footer Link Component
const FooterLink = ({ to, children, external = false }: { to: string; children: React.ReactNode; external?: boolean }) => {
  if (external) {
    return (
      <motion.li whileHover={{ x: 5 }} transition={{ type: "spring", stiffness: 400, damping: 10 }}>
        <a
          href={to}
          target="_blank"
          rel="noopener noreferrer"
          className="text-gray-400 hover:text-primary-400 transition-colors flex items-center"
        >
          {children}
        </a>
      </motion.li>
    );
  }
  
  return (
    <motion.li whileHover={{ x: 5 }} transition={{ type: "spring", stiffness: 400, damping: 10 }}>
      <Link to={to} className="text-gray-400 hover:text-primary-400 transition-colors">
        {children}
      </Link>
    </motion.li>
  );
};

// Social Link Component
const SocialLink = ({ href, icon }: { href: string; icon: React.ReactNode }) => {
  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="w-10 h-10 rounded-full bg-dark-700 hover:bg-primary-600 flex items-center justify-center text-gray-400 hover:text-white transition-all duration-300 shadow-3d"
      aria-label="Social media link"
      whileHover={{ scale: 1.1, rotate: 5 }}
      whileTap={{ scale: 0.95 }}
    >
      {icon}
    </motion.a>
  );
};

// Animated Scroll Button
const AnimatedScrollButton = ({ show, onClick }: { show: boolean; onClick: () => void }) => {
  return (
    <motion.button
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: show ? 1 : 0, y: show ? 0 : 20 }}
      transition={{ duration: 0.3 }}
      onClick={onClick}
      className="fixed bottom-8 right-8 w-12 h-12 rounded-full bg-primary-600 hover:bg-primary-500 shadow-3d flex items-center justify-center text-white z-50"
      aria-label="Scroll to top"
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
      style={{ display: show ? 'flex' : 'none' }}
    >
      <FiArrowUp size={20} />
    </motion.button>
  );
};

export default Footer;